import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import "./Admin.css";
import EditModal from "./modals/EditModal";
import PlantEditForm from "./forms/PlantEditForm";
import ObjectEditForm from "./forms/ObjectEditForm";
import CompanyEditForm, { CompanyEditFormRef } from "./forms/CompanyEditForm";
import InventoryEditForm, {
  InventoryEditFormRef,
} from "./forms/InventoryEditForm";
import { useAuthenticatedApi } from "../hooks/useAuthenticatedApi";
import { useAuth } from "../hooks/useAuth";
import { Plant, GardenObject, Company, InventoryItem } from "./types";
import { apiClient } from "@/utils/apiClient/apiClient";

// const API_URL = import.meta.env.VITE_API_URL;
// const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS;

const TABS = ["plants", "objects", "companies", "inventory"];
const LOCAL_STORAGE_TAB_KEY = "adminActiveTab";
const LOCAL_STORAGE_COMPANY_KEY = "adminSelectedCompany";

const Admin: React.FC = () => {
  const { t } = useTranslation();

  // Get initial tab from localStorage if available
  const getInitialTab = () => {
    const savedTab = localStorage.getItem(LOCAL_STORAGE_TAB_KEY);
    return savedTab && TABS.includes(savedTab) ? savedTab : "plants";
  };

  // Get initial company from localStorage if available and we're on inventory tab
  const getInitialCompany = () => {
    const savedTab = localStorage.getItem(LOCAL_STORAGE_TAB_KEY);
    if (savedTab === "inventory") {
      const savedCompany = localStorage.getItem(LOCAL_STORAGE_COMPANY_KEY);
      return savedCompany || "";
    }
    return "";
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [selectedCompany, setSelectedCompany] = useState<string>(
    getInitialCompany()
  );

  // Get authenticated API client
  const api = useAuthenticatedApi();
  const { isAuthenticated } = useAuth();

  // Individual loading states for each tab
  const [loadingPlants, setLoadingPlants] = useState<boolean>(false);
  const [loadingObjects, setLoadingObjects] = useState<boolean>(false);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [loadingInventory, setLoadingInventory] = useState<boolean>(false);

  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [plants, setPlants] = useState<Plant[]>([]);
  const [objects, setObjects] = useState<GardenObject[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);

  // Search state for plants
  const [plantSearchTerm, setPlantSearchTerm] = useState<string>("");
  const [plantSearchField, setPlantSearchField] = useState<string>("all");

  // Search state for objects
  const [objectSearchTerm, setObjectSearchTerm] = useState<string>("");
  const [objectSearchField, setObjectSearchField] = useState<string>("all");

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [editType, setEditType] = useState("");
  const [editItemId, setEditItemId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveButtonLabel, setSaveButtonLabel] = useState("Save Changes");

  // Selected items for editing
  const [selectedPlant, setSelectedPlant] = useState<Plant | null>(null);
  const [selectedObject, setSelectedObject] = useState<GardenObject | null>(
    null
  );
  const [selectedCompanyForEdit, setSelectedCompanyForEdit] =
    useState<Company | null>(null);
  const [selectedInventoryItem, setSelectedInventoryItem] =
    useState<InventoryItem | null>(null);

  // Refs for form components
  const companyFormRef = useRef<CompanyEditFormRef>(null);
  const inventoryFormRef = useRef<InventoryEditFormRef>(null);

  // Object catalog settings state
  const [objectTypes, setObjectTypes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // Clear errors and success messages when switching tabs or opening modal
  const clearMessagesLocal = () => {
    setError(null);
    setIsError(false);
    setSaveError(null);
    setSuccessMessage(null);
  };

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      timers.push(timer);
    }

    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      timers.push(timer);
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [error, successMessage]);

  const verifyPlant = (plant: Plant) => {
    // Get the keys of the plant object
    const plantKeys = Object.keys(plant);
    const invalidKeys: Array<string> = [];
    plantKeys.forEach((v, i) => {
      switch (v) {
        case "common_name":
          if (plant["common_name"].length < 1) {
            invalidKeys.push(t("admin.validation.commonNameRequired"));
          }
          return;
        case "scientific_name":
          if (plant["scientific_name"].length < 1) {
            invalidKeys.push(t("admin.validation.scientificNameRequired"));
          }
          return;
        case "genus":
          if (plant["genus"].length < 1) {
            invalidKeys.push(t("admin.validation.genusRequired"));
          }
          return;
        case "family":
          if (plant["family"].length < 1) {
            invalidKeys.push(t("admin.validation.familyRequired"));
          }
          return;
      }
    });
    if (invalidKeys.length > 0) {
      return { success: false, invalidKeys };
    } else {
      return { success: true };
    }
  };

  const verifyObject = (object: GardenObject) => {
    // Get the keys of the plant object
    const objectKeys = Object.keys(object);
    const invalidKeys: Array<string> = [];
    objectKeys.forEach((v, i) => {
      switch (v) {
        case "name":
          if (object["name"].length < 1) {
            invalidKeys.push(t("admin.validation.nameRequired"));
          }
          return;
        case "type":
          if (object["type"].length < 1) {
            invalidKeys.push(t("admin.validation.typeRequired"));
          } else if (!objectTypes.includes(object["type"])) {
            invalidKeys.push(
              t("admin.validation.typeInvalid", {
                types: objectTypes.join(", "),
              })
            );
          }
          return;
        case "category":
          if (object["category"].length < 1) {
            invalidKeys.push(t("admin.validation.categoryRequired"));
          } else if (!categories.includes(object["category"])) {
            invalidKeys.push(
              t("admin.validation.categoryInvalid", {
                categories: categories.join(", "),
              })
            );
          }
          return;
      }
    });
    if (invalidKeys.length > 0) {
      return { success: false, invalidKeys };
    } else {
      return { success: true };
    }
  };

  const fetchPlants = async () => {
    if (!isAuthenticated) {
      setError(t("admin.errorMessages.authenticationRequired"));
      setIsError(true);
      setPlants([]);
      return;
    }

    setLoadingPlants(true);
    clearMessagesLocal();

    try {
      const plants = await api.getPlants(null);
      if (process.env.NODE_ENV === "development") {
        console.log("Plants fetched:", plants);
      }

      if (Array.isArray(plants)) {
        setPlants(plants);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.error("Unexpected response format:", plants);
        }
        setError(t("admin.errorMessages.unexpectedResponseFormat"));
        setIsError(true);
        setPlants([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching plants:", error);
      }
      setError(t("admin.errorMessages.networkErrorPlants"));
      setIsError(true);
      setPlants([]);
    } finally {
      setLoadingPlants(false);
    }
  };

  const fetchObjects = async () => {
    if (!isAuthenticated) {
      setError(t("admin.errorMessages.authenticationRequired"));
      setIsError(true);
      setObjects([]);
      return;
    }

    setLoadingObjects(true);
    clearMessagesLocal();
    try {
      const objects = await api.getObjects(null);
      if (process.env.NODE_ENV === "development") {
        console.log("Objects fetched:", objects);
      }

      if (Array.isArray(objects)) {
        setObjects(objects);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.error("Unexpected response format:", objects);
        }
        setError(t("admin.errorMessages.unexpectedResponseFormat"));
        setIsError(true);
        setObjects([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching objects:", error);
      }
      setError(t("admin.errorMessages.networkErrorObjects"));
      setIsError(true);
      setObjects([]);
    } finally {
      setLoadingObjects(false);
    }
  };

  const fetchCompanies = async () => {
    console.log("fetchCompanies called, isAuthenticated:", isAuthenticated);
    if (!isAuthenticated) {
      setError("Authentication required to fetch companies");
      setIsError(true);
      setCompanies([]);
      return;
    }

    console.log("Setting loadingCompanies to true");
    setLoadingCompanies(true);
    clearMessagesLocal();

    try {
      const companies = await api.getCompanies();
      if (process.env.NODE_ENV === "development") {
        console.log("Companies fetched:", companies);
      }

      if (Array.isArray(companies)) {
        // Transform backend data to match frontend interface if needed
        const transformedCompanies = companies.map((company: any) => {
          // Parse JSON strings back to objects if needed
          let address = company.address;
          let service_areas = company.service_areas;
          let business_hours = company.business_hours;

          // Handle address parsing
          if (typeof company.address === "string") {
            try {
              address = JSON.parse(company.address);
            } catch (e) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Failed to parse address JSON:", company.address);
              }
              address = undefined;
            }
          }

          // Handle service_areas parsing
          if (typeof company.service_areas === "string") {
            try {
              service_areas = JSON.parse(company.service_areas);
            } catch (e) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "Failed to parse service_areas JSON:",
                  company.service_areas
                );
              }
              service_areas = undefined;
            }
          }

          // Handle business_hours parsing
          if (typeof company.business_hours === "string") {
            try {
              business_hours = JSON.parse(company.business_hours);
            } catch (e) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "Failed to parse business_hours JSON:",
                  company.business_hours
                );
              }
              business_hours = undefined;
            }
          }

          return {
            company_id: company.company_id,
            name: company.name,
            contact_name: company.contact_name || "",
            contact_email: company.contact_email,
            contact_phone: company.contact_phone,
            website: company.website,
            logo: company.logo,
            address: address,
            service_areas: service_areas,
            business_hours: business_hours,
            timestamps: company.timestamps,
          };
        });
        console.log("Setting companies:", transformedCompanies.length);
        setCompanies(transformedCompanies);

        // Validate that the saved company still exists
        const savedCompany = localStorage.getItem(LOCAL_STORAGE_COMPANY_KEY);
        if (
          savedCompany &&
          !transformedCompanies.some((c) => c.company_id === savedCompany)
        ) {
          // Saved company no longer exists, clear it
          localStorage.removeItem(LOCAL_STORAGE_COMPANY_KEY);
          if (activeTab === "inventory") {
            setSelectedCompany("");
          }
        } else if (
          savedCompany &&
          transformedCompanies.some((c) => c.company_id === savedCompany) &&
          activeTab === "inventory" &&
          !selectedCompany // Only set if not already set
        ) {
          // Saved company exists and we're on inventory tab, make sure it's selected
          setSelectedCompany(savedCompany);
        }
      } else {
        if (process.env.NODE_ENV === "development") {
          console.error("Unexpected response format:", companies);
        }
        setError("Unexpected response format from server");
        setIsError(true);
        setCompanies([]);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching companies:", error);
      }

      // Handle GraphQL errors
      if (error.errors && error.errors.length > 0) {
        const graphQLError = error.errors[0];
        setError(
          `Failed to load companies: ${graphQLError.message || "GraphQL error"}`
        );
      } else if (error.message) {
        setError(`Failed to load companies: ${error.message}`);
      } else {
        setError(t("admin.errorMessages.networkErrorCompanies"));
      }
      setIsError(true);
      setCompanies([]);
    } finally {
      console.log("Setting loadingCompanies to false");
      setLoadingCompanies(false);
    }
  };

  const fetchInventory = async (companyId: string) => {
    if (!companyId) return;
    if (!isAuthenticated) {
      setError("Authentication required to fetch inventory");
      setIsError(true);
      setInventory([]);
      return;
    }

    setLoadingInventory(true);
    clearMessagesLocal();

    try {
      const inventoryData = await api.listInventory(companyId);
      if (process.env.NODE_ENV === "development") {
        console.log("Inventory fetched:", inventoryData);
      }

      if (Array.isArray(inventoryData)) {
        setInventory(inventoryData);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.error("Unexpected response format:", inventoryData);
        }
        setError("Unexpected response format from server");
        setIsError(true);
        setInventory([]);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching inventory:", error);
      }

      // Check if it's a 400 error which might indicate the Lambda function isn't deployed yet
      if (error?.message?.includes("400") || error?.statusCode === 400) {
        setError(
          "Inventory API is being deployed. Please wait a moment and try again."
        );
      } else {
        setError(t("admin.errorMessages.networkErrorInventory"));
      }
      setIsError(true);
      setInventory([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const createNewItem = (type: string) => {
    setEditType(type);
    setEditItemId("");

    switch (type) {
      case "plant":
        setModalTitle(t("admin.modalTitles.addPlant"));
        setSaveButtonLabel("Add Plant");
        setSelectedPlant({
          plant_id: undefined,
          common_name: "",
          scientific_name: "",
          family: "",
          genus: "",
        });
        break;
      case "object":
        setModalTitle(t("admin.modalTitles.addObject"));
        setSaveButtonLabel("Add Object");
        setSelectedObject({
          object_id: "",
          name: "",
          type: "",
          category: "",
        });
        break;
      case "company":
        setModalTitle(t("admin.modalTitles.addCompany"));
        setSaveButtonLabel("Add Company");
        setSelectedCompanyForEdit({
          company_id: "",
          name: "",
          description: "",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          website: "",
          logo: "",
          address: {
            street: "",
            city: "",
            state: "",
            zip_code: "",
            country: "",
          },
          service_areas: [],
          business_hours: {},
          timestamps: {},
        });
        break;
      case "inventory":
        setModalTitle(t("admin.modalTitles.addInventory"));
        setSaveButtonLabel("Add Item");
        setSelectedInventoryItem(null);
        break;
    }

    setIsEditModalOpen(true);
  };

  const editItem = (type: string, id: string) => {
    setEditType(type);
    setEditItemId(id);

    switch (type) {
      case "plant":
        const plant = plants.find((p) => p.plant_id === id);
        if (plant) {
          setModalTitle(
            t("admin.modalTitles.editPlant", { name: plant.common_name })
          );
          setSaveButtonLabel("Update Plant");
          setSelectedPlant(plant);
          setIsEditModalOpen(true);
        }
        break;
      case "object":
        const obj = objects.find((o) => o.object_id === id);
        if (obj) {
          setModalTitle(t("admin.modalTitles.editObject", { name: obj.name }));
          setSaveButtonLabel("Update Object");
          setSelectedObject(obj);
          setIsEditModalOpen(true);
        }
        break;
      case "company":
        const company = companies.find((c) => c.company_id === id);
        if (company) {
          setModalTitle(
            t("admin.modalTitles.editCompany", { name: company.name })
          );
          setSaveButtonLabel("Update Company");
          setSelectedCompanyForEdit(company);
          setIsEditModalOpen(true);
        }
        break;
      case "inventory":
        const invItem = inventory.find((i) => i.inventory_id === id);
        if (invItem) {
          setModalTitle(
            t("admin.modalTitles.editInventory", { name: invItem.name })
          );
          setSaveButtonLabel("Update Item");
          setSelectedInventoryItem(invItem);
          setIsEditModalOpen(true);
        }
        break;
    }
  };

  const confirmDelete = async (type: string, id: string) => {
    if (window.confirm(t("admin.confirmDelete", { type }))) {
      clearMessagesLocal();

      try {
        switch (type) {
          case "plant":
            await delete_plant(id);
            setPlants(plants.filter((p) => p.plant_id !== id));
            setSuccessMessage(t("admin.successMessages.plantDeleted"));
            break;
          case "object":
            await delete_object(id);
            setObjects(objects.filter((o) => o.object_id !== id));
            setSuccessMessage(t("admin.successMessages.objectDeleted"));
            break;
          case "company":
            await delete_company(id);
            setCompanies(companies.filter((c) => c.company_id !== id));
            setSuccessMessage(t("admin.successMessages.companyDeleted"));
            break;
          case "inventory":
            await delete_inventory_item(selectedCompany, id);
            setInventory(inventory.filter((i) => i.inventory_id !== id));
            setSuccessMessage(t("admin.successMessages.inventoryDeleted"));
            break;
        }
      } catch (error) {
        console.error(`Failed to delete ${type}:`, error);
        setError(t("admin.errorMessages.deleteError"));
        setIsError(true);
      }
    }
  };

  const savePlant = async (plant: Plant) => {
    if (!isAuthenticated) {
      setSaveError(t("admin.errorMessages.authenticationRequired"));
      return;
    }

    let verifyObj = verifyPlant(plant);

    if (verifyObj.success === false && verifyObj.invalidKeys !== undefined) {
      setSaveError(
        verifyObj.invalidKeys.reduce((prev, v) => prev + "\n" + v, "")
        // verifyObj.invalidKeys.reduce(
        //   (prev, v) =>
        //     prev +
        //     `\n'${v
        //       .split("_")
        //       .reduce(
        //         (prev, curr, i) =>
        //           prev +
        //           (i === 0 ? "" : " ") +
        //           curr[0].toUpperCase() +
        //           curr.substring(1, curr.length),
        //         ""
        //       )}' cannot be empty`,
        //   ""
        // )
      );
      return;
    }

    setIsSaving(true);
    clearMessagesLocal();
    try {
      if (editItemId) {
        // Update existing plant
        const response = await api.updatePlant(editItemId, plant);

        if (response && response.data && response.data.plant_id) {
          // Update existing plant in local state by merging with server data
          setPlants(
            plants.map((p) =>
              p.plant_id === editItemId ? { ...p, ...response.data } : p
            )
          );
          setSuccessMessage(
            response.message || t("admin.successMessages.plantUpdated")
          );
          setIsEditModalOpen(false);
        } else {
          setSaveError(t("admin.errorMessages.updatePlant"));
        }
      } else {
        // Create new plant - remove plant_id from request body
        const { plant_id, ...plantData } = plant;
        const response = await api.createPlant(plantData);

        if (response && response.data && response.data.plant_id) {
          // Add new plant to local state with server data
          setPlants([...plants, response.data]);
          setSuccessMessage(
            response.message || t("admin.successMessages.plantCreated")
          );

          setIsEditModalOpen(false);
        } else {
          setSaveError(t("admin.errorMessages.savePlant"));
        }
      }
    } catch (error) {
      console.error("Error saving plant:", error);
      setSaveError(t("admin.errorMessages.networkSaveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const delete_plant = async (plant_id: string) => {
    if (!isAuthenticated) {
      throw new Error("Authentication required to delete plants");
    }

    const response = await api.deletePlant(plant_id);

    if (response) {
      console.log(`Plant ${plant_id} deleted successfully`);
    } else {
      console.error(`Failed to delete plant ${plant_id}`);
      throw new Error(
        `Failed to delete plant: ${response?.error || "Unknown error"}`
      );
    }
  };

  const delete_object = async (object_id: string) => {
    if (!isAuthenticated) {
      throw new Error("Authentication required to delete objects");
    }

    await apiClient.deleteObject(object_id);
    console.log(`Object ${object_id} deleted successfully`);
  };

  const delete_company = async (company_id: string) => {
    if (!isAuthenticated) {
      throw new Error("Authentication required to delete companies");
    }

    const response = await apiClient.deleteCompany(company_id);

    if (response) {
      console.log(`Company ${company_id} deleted successfully`);
    } else {
      console.error(`Failed to delete company ${company_id}`);
      throw new Error(
        `Failed to delete company: ${response?.error || "Unknown error"}`
      );
    }
  };

  const delete_inventory_item = async (company_id: string, item_id: string) => {
    if (!isAuthenticated) {
      throw new Error("Authentication required to delete inventory items");
    }

    const response = await api.deleteInventoryItem(item_id);

    if (response) {
      console.log(`Inventory item ${item_id} deleted successfully`);
    } else {
      console.error(`Failed to delete inventory item ${item_id}`);

      // Check if it's a 400 error which might indicate the Lambda function isn't deployed yet
      if (response?.error?.includes("400") || response?.statusCode === 400) {
        throw new Error(
          "Inventory API is being deployed. Please wait a moment and try again."
        );
      } else {
        throw new Error(
          `Failed to delete inventory item: ${
            response?.error || "Unknown error"
          }`
        );
      }
    }
  };

  const saveObject = async (object: GardenObject) => {
    let verifyObj = verifyObject(object);

    if (verifyObj.success === false && verifyObj.invalidKeys !== undefined) {
      setSaveError(
        verifyObj.invalidKeys.reduce((prev, v) => prev + "\n" + v, "")
      );
      return;
    }

    setIsSaving(true);
    clearMessagesLocal();
    try {
      if (editItemId) {
        // update existing object
        const response = await apiClient.updateObject(editItemId, object);

        if (response && response.data && response.data.object_id) {
          // Update existing object in local state by merging with server data
          setObjects(
            objects.map((p) =>
              p.object_id === editItemId ? { ...p, ...response.data } : p
            )
          );
          setSuccessMessage(t("admin.successMessages.objectUpdated"));
          setIsEditModalOpen(false);
        } else {
          setSaveError(t("admin.errorMessages.updateObject"));
        }
      } else {
        // Create new object - remove object_id from request body
        const { object_id, ...objectData } = object;

        try {
          const response = await apiClient.createObject(objectData);

          if (response && response.data && response.data.object_id) {
            // Add new object to local state with server data
            setObjects([...objects, response.data]);
            setSuccessMessage(t("admin.successMessages.objectCreated"));
            setIsEditModalOpen(false);
          } else {
            throw new Error(t("admin.errorMessages.saveObject"));
          }
        } catch (error: any) {
          setSaveError(error.message);
        }
      }
    } catch (error) {
      console.error("Error saving object:", error);
      setSaveError(t("admin.errorMessages.networkSaveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const saveCompany = async (company: Company) => {
    if (!isAuthenticated) {
      setSaveError("Authentication required to save companies");
      return;
    }

    // Basic validation
    if (!company.name || company.name.trim() === "") {
      setSaveError("Company name is required");
      return;
    }

    setIsSaving(true);
    clearMessagesLocal();

    try {
      if (editItemId) {
        // Update existing company
        const { company_id, ...companyData } = company;
        const response = await apiClient.updateCompany(editItemId, companyData);

        if (response && response.data && response.data.company_id) {
          // Update existing company in local state by merging with server data
          setCompanies(
            companies.map((c) =>
              c.company_id === editItemId ? { ...c, ...response.data } : c
            )
          );
          setSuccessMessage(t("admin.successMessages.companyUpdated"));
          setIsEditModalOpen(false);
        } else {
          setSaveError(
            "Failed to update company: Invalid response from server"
          );
        }
      } else {
        // Create new company - remove company_id from request body
        const { company_id, ...companyData } = company;
        const response = await apiClient.createCompany(companyData);

        if (response && response.data && response.data.company_id) {
          // Add new company to local state with server data
          setCompanies([...companies, response.data]);
          setSuccessMessage(t("admin.successMessages.companyCreated"));
          setIsEditModalOpen(false);
        } else {
          setSaveError(t("admin.errorMessages.saveCompany"));
        }
      }
    } catch (error: any) {
      console.error("Error saving company:", error);

      // Handle GraphQL errors
      if (error.errors && error.errors.length > 0) {
        const graphQLError = error.errors[0];
        setSaveError(
          `Failed to save company: ${graphQLError.message || "GraphQL error"}`
        );
      } else if (error.message) {
        setSaveError(`Failed to save company: ${error.message}`);
      } else {
        setSaveError(t("admin.errorMessages.networkSaveError"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveInventoryItem = async (item: InventoryItem) => {
    if (!isAuthenticated) {
      setSaveError("Authentication required to save inventory items");
      return;
    }
    console.warn("item:", item);
    // Client-side validation
    if (
      !item.type ||
      item.type.trim() === "" ||
      item.type === "Select Type" ||
      !["plant", "object"].includes(item.type)
    ) {
      setSaveError("Please select an item type (Plant or Object)");
      return;
    }

    if (!item.item_id || item.item_id.trim() === "") {
      setSaveError("Please select a plant or object item");
      return;
    }

    const quantity =
      typeof item.quantity === "string"
        ? parseFloat(item.quantity)
        : item.quantity;
    if (quantity < 0) {
      setSaveError("Quantity cannot be negative");
      return;
    }

    const sellingPrice =
      typeof item.selling_price === "string"
        ? parseFloat(item.selling_price)
        : item.selling_price;
    if (sellingPrice < 0) {
      setSaveError("Selling price cannot be negative");
      return;
    }

    setIsSaving(true);
    clearMessagesLocal();

    try {
      if (editItemId) {
        // Update existing inventory item
        const updateData = {
          type: item.type,
          name: item.name,
          quantity:
            item.quantity === "" || item.quantity === undefined
              ? 0
              : Number(item.quantity),
          unit_cost:
            item.unit_cost === "" || item.unit_cost === undefined
              ? 0
              : Number(item.unit_cost || 0),
          customer_price:
            item.selling_price === "" || item.selling_price === undefined
              ? 0
              : Number(item.selling_price),
          supplier: item.supplier || "",
          restock_threshold:
            item.restock_threshold === "" ||
            item.restock_threshold === undefined
              ? 0
              : Number(item.restock_threshold || 0),
          low_stock_threshold:
            item.low_stock_threshold === "" ||
            item.low_stock_threshold === undefined
              ? 0
              : Number(item.low_stock_threshold || 0),
          location_in_warehouse: item.location_in_warehouse || "",
          status: item.status || "active",
        };

        const response = await api.updateInventoryItem(editItemId, updateData);

        if (response && response.data) {
          // Update existing item in local state by merging with server data
          setInventory(
            inventory.map((i) =>
              i.inventory_id === editItemId ? { ...i, ...response.data } : i
            )
          );
          setSuccessMessage(t("admin.successMessages.inventoryUpdated"));
          setIsEditModalOpen(false);
        } else {
          setSaveError(
            "Failed to update inventory item: Invalid response from server"
          );
        }
      } else {
        // Create new inventory item
        const inventoryData = {
          company_id: selectedCompany, // company_id
          inventory_id: item.inventory_id, // item_id - let backend generate if not provided
          type: item.type,
          item_id: item.item_id,
          quantity:
            item.quantity === "" || item.quantity === undefined
              ? 0
              : Number(item.quantity),
          unit_cost:
            item.unit_cost === "" || item.unit_cost === undefined
              ? 0
              : Number(item.unit_cost || 0),
          customer_price:
            item.selling_price === "" || item.selling_price === undefined
              ? 0
              : Number(item.selling_price),
          supplier: item.supplier || "",
          restock_threshold:
            item.restock_threshold === "" ||
            item.restock_threshold === undefined
              ? 0
              : Number(item.restock_threshold || 0),
          low_stock_threshold:
            item.low_stock_threshold === "" ||
            item.low_stock_threshold === undefined
              ? 0
              : Number(item.low_stock_threshold || 0),
          location_in_warehouse: item.location_in_warehouse || "",
          status: item.status || "active",
          timestamps: {
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        };

        const response = await api.createInventoryItem(inventoryData);
        console.log("response: \n", response);
        if (response && response.inventory_id) {
          // Add new item to local state with server data
          const newItem = {
            ...response,
            inventory_id: response.inventory_id,
            company_id: response.company_id,
            item_id: response.item_id,
          };

          setInventory([...inventory, newItem]);
          setSuccessMessage(t("admin.successMessages.inventoryCreated"));
          setIsEditModalOpen(false);
        } else {
          setSaveError(t("admin.errorMessages.saveInventory"));
        }
      }
    } catch (error: any) {
      console.error("Error saving inventory item:", error);

      // Check if it's a 400 error which might indicate the Lambda function isn't deployed yet
      if (error?.message?.includes("400") || error?.statusCode === 400) {
        setSaveError(
          "Inventory API is being deployed. Please wait a moment and try again."
        );
      } else {
        setSaveError(t("admin.errorMessages.networkSaveError"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    switch (editType) {
      case "plant":
        if (selectedPlant) savePlant(selectedPlant);
        break;
      case "object":
        if (selectedObject) saveObject(selectedObject);
        break;
      case "company":
        if (companyFormRef.current) {
          const formData = companyFormRef.current.getFormData();
          saveCompany(formData);
        }
        break;
      case "inventory":
        if (inventoryFormRef.current) {
          const formData = inventoryFormRef.current.getFormData();
          saveInventoryItem(formData);
        }
        break;
    }
  };

  const closeModal = () => {
    setIsEditModalOpen(false);
    setSelectedPlant(null);
    setSelectedObject(null);
    setSelectedCompanyForEdit(null);
    setSelectedInventoryItem(null);
    setSaveError(null); // Clear save errors when modal is closed
    setSuccessMessage(null); // Clear success messages when modal is closed
    setSaveButtonLabel("Save Changes"); // Reset button label

    // Clear form refs
    // Note: The useEffect in CompanyEditForm will handle resetting
    // when selectedCompanyForEdit becomes null
  };

  // Helper to get loading state for the current tab
  const getCurrentLoading = () => {
    switch (activeTab) {
      case "plants":
        return loadingPlants;
      case "objects":
        return loadingObjects;
      case "companies":
        return loadingCompanies;
      case "inventory":
        return loadingInventory;
      default:
        return false;
    }
  };

  // Reusable search component
  const renderSearchBar = (
    searchTerm: string,
    setSearchTerm: (term: string) => void,
    searchField: string,
    setSearchField: (field: string) => void,
    fieldOptions: { value: string; label: string }[],
    placeholder: string,
    resultsCount: number,
    totalCount: number
  ) => {
    return (
      <div className="search-container">
        <div className="input-group search-input-group">
          <select
            className="form-select search-field-select"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            {fieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={
              searchField === "all"
                ? placeholder
                : `Search in ${searchField.replace("_", " ")}...`
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="btn btn-outline-secondary clear-search-btn"
              type="button"
              onClick={() => setSearchTerm("")}
            >
              {t("common.clear")}
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-info">
            <small className="text-muted">
              {resultsCount} results
              {searchField !== "all" && (
                <span> in {searchField.replace("_", " ")}</span>
              )}
            </small>
          </div>
        )}
      </div>
    );
  };

  // Helper to filter plants based on search term and field
  const getFilteredPlants = () => {
    if (!plantSearchTerm.trim()) {
      return plants;
    }

    const searchTerm = plantSearchTerm.toLowerCase().trim();

    return plants.filter((plant) => {
      // If searching in all fields
      if (plantSearchField === "all") {
        // Search in common_name
        if (plant.common_name.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in scientific_name
        if (plant.scientific_name.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in genus
        if (plant.genus.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in family
        if (plant.family.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in tags
        if (
          plant.tags &&
          plant.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
        ) {
          return true;
        }

        return false;
      }

      // Field-specific search
      switch (plantSearchField) {
        case "common_name":
          return plant.common_name.toLowerCase().includes(searchTerm);
        case "scientific_name":
          return plant.scientific_name.toLowerCase().includes(searchTerm);
        case "genus":
          return plant.genus.toLowerCase().includes(searchTerm);
        case "family":
          return plant.family.toLowerCase().includes(searchTerm);
        case "tags":
          return (
            plant.tags &&
            plant.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
          );
        default:
          return false;
      }
    });
  };

  // Helper to filter objects based on search term and field
  const getFilteredObjects = () => {
    if (!objectSearchTerm.trim()) {
      return objects;
    }

    const searchTerm = objectSearchTerm.toLowerCase().trim();

    return objects.filter((object) => {
      // If searching in all fields
      if (objectSearchField === "all") {
        // Search in name
        if (object.name.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in type
        if (object.type.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in category
        if (object.category.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in tags
        if (
          object.tags &&
          object.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
        ) {
          return true;
        }

        return false;
      }

      // Field-specific search
      switch (objectSearchField) {
        case "name":
          return object.name.toLowerCase().includes(searchTerm);
        case "type":
          return object.type.toLowerCase().includes(searchTerm);
        case "category":
          return object.category.toLowerCase().includes(searchTerm);
        case "tags":
          return (
            object.tags &&
            object.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
          );
        default:
          return false;
      }
    });
  };

  useEffect(() => {
    // Only fetch for the initial tab
    if (activeTab === "plants") {
      fetchPlants();
    } else if (activeTab === "objects") {
      fetchObjects();
    } else if (activeTab === "companies") {
      fetchCompanies();
    } else if (activeTab === "inventory" && selectedCompany) {
      fetchInventory(selectedCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(
      `Tab changed to: ${activeTab}, companies: ${companies.length}, loadingCompanies: ${loadingCompanies}`
    );

    if (activeTab === "plants" && plants.length === 0 && !loadingPlants) {
      fetchPlants();
    } else if (
      activeTab === "objects" &&
      objects.length === 0 &&
      !loadingObjects
    ) {
      fetchObjects();
    } else if (
      activeTab === "companies" &&
      companies.length === 0 &&
      !loadingCompanies
    ) {
      console.log("Fetching companies...");
      fetchCompanies();
    } else if (activeTab === "inventory") {
      // Load companies if not already loaded (needed for inventory dropdown)
      if (companies.length === 0 && !loadingCompanies) {
        console.log("Fetching companies for inventory tab...");
        fetchCompanies();
      }

      // Load inventory if company is selected
      if (selectedCompany && !loadingInventory) {
        fetchInventory(selectedCompany);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "inventory" && selectedCompany && !loadingInventory) {
      fetchInventory(selectedCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_TAB_KEY, activeTab);
  }, [activeTab]);

  // Save selectedCompany to localStorage whenever it changes
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem(LOCAL_STORAGE_COMPANY_KEY, selectedCompany);
    } else {
    }
  }, [selectedCompany]);

  // Handle company selection when switching tabs
  useEffect(() => {
    if (activeTab === "inventory") {
      // When switching TO inventory tab, check localStorage for saved company
      const savedCompany = localStorage.getItem(LOCAL_STORAGE_COMPANY_KEY);
      if (
        savedCompany &&
        companies.length > 0 &&
        companies.some((c) => c.company_id === savedCompany)
      ) {
        setSelectedCompany(savedCompany);
      } else if (savedCompany && companies.length === 0) {
        // Companies not loaded yet, set it anyway and it will be validated when companies load
        setSelectedCompany(savedCompany);
      }
    } else if (activeTab !== "inventory" && selectedCompany) {
      // When switching AWAY from inventory tab, clear the UI state but keep localStorage
      // setSelectedCompany("");
    }
  }, [activeTab, companies, selectedCompany]);

  // Update function for the plant table to include image thumbnail column
  const renderPlantTable = () => {
    const filteredPlants = getFilteredPlants();

    return (
      <div>
        {/* Search input */}
        {renderSearchBar(
          plantSearchTerm,
          setPlantSearchTerm,
          plantSearchField,
          setPlantSearchField,
          [
            { value: "all", label: t("admin.searchFieldOptions.all") },
            {
              value: "common_name",
              label: t("admin.searchFieldOptions.common_name"),
            },
            {
              value: "scientific_name",
              label: t("admin.searchFieldOptions.scientific_name"),
            },
            { value: "genus", label: t("admin.searchFieldOptions.genus") },
            { value: "family", label: t("admin.searchFieldOptions.family") },
            { value: "tags", label: t("admin.searchFieldOptions.tags") },
          ],
          t("admin.searchPlantsPlaceholder"),
          filteredPlants.length,
          plants.length
        )}

        {filteredPlants.length === 0 ? (
          <div className="text-center py-5">
            {isError ? (
              <p className="error-message-tab">
                {t("admin.errorMessages.loadPlants")}
              </p>
            ) : plantSearchTerm ? (
              <div>
                <p>{t("admin.noPlantsMatching", { term: plantSearchTerm })}</p>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setPlantSearchTerm("")}
                >
                  {t("common.clear")} {t("common.search")}
                </button>
              </div>
            ) : (
              <p>{t("admin.noPlants")}</p>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t("admin.image")}</th>
                  <th>{t("admin.commonName")}</th>
                  <th>{t("admin.scientificName")}</th>
                  <th>{t("admin.genus")}</th>
                  <th>{t("admin.family")}</th>
                  <th>{t("admin.tag")}</th>
                  <th>{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlants.map((plant) => (
                  <tr key={plant.plant_id}>
                    <td>
                      {plant.image_url ? (
                        <img
                          src={plant.image_url}
                          alt={plant.common_name}
                          className="thumbnail-image"
                          title={plant.common_name}
                        />
                      ) : (
                        <div className="no-thumbnail">No Image</div>
                      )}
                    </td>
                    <td>{plant.common_name}</td>
                    <td>
                      <i>{plant.scientific_name}</i>
                    </td>
                    <td>{plant.genus}</td>
                    <td>{plant.family}</td>
                    <td>
                      {plant.tags && plant.tags.length > 0 ? (
                        <div className="tag-badges-container">
                          {plant.tags.map((tag, index) => (
                            <span key={index} className="badge bg-secondary">
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">
                          {t("admin.searchFieldOptions.noTags")}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() =>
                          plant.plant_id && editItem("plant", plant.plant_id)
                        }
                        disabled={!plant.plant_id}
                      >
                        {t("admin.edit")}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          plant.plant_id &&
                          confirmDelete("plant", plant.plant_id)
                        }
                        disabled={!plant.plant_id}
                      >
                        {t("admin.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Update function for the object table to include image thumbnail column
  const renderObjectTable = () => {
    const filteredObjects = getFilteredObjects();

    return (
      <div>
        {/* Search input */}
        {renderSearchBar(
          objectSearchTerm,
          setObjectSearchTerm,
          objectSearchField,
          setObjectSearchField,
          [
            { value: "all", label: t("admin.searchFieldOptions.all") },
            { value: "name", label: t("admin.searchFieldOptions.name") },
            { value: "type", label: t("admin.searchFieldOptions.type") },
            {
              value: "category",
              label: t("admin.searchFieldOptions.category"),
            },
            { value: "tags", label: t("admin.searchFieldOptions.tags") },
          ],
          t("admin.searchObjectsPlaceholder"),
          filteredObjects.length,
          objects.length
        )}

        {filteredObjects.length === 0 ? (
          <div className="text-center py-5">
            {isError ? (
              <p className="error-message-tab">
                {t("admin.errorMessages.loadObjects")}
              </p>
            ) : objectSearchTerm ? (
              <div>
                <p>
                  {t("admin.noObjectsMatching", { term: objectSearchTerm })}
                </p>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setObjectSearchTerm("")}
                >
                  {t("common.clear")} {t("common.search")}
                </button>
              </div>
            ) : (
              <p>{t("admin.noObjects")}</p>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t("admin.image")}</th>
                  <th>{t("admin.name")}</th>
                  <th>{t("admin.type")}</th>
                  <th>{t("admin.category")}</th>
                  <th>{t("admin.tag")}</th>
                  <th>{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredObjects.map((obj) => (
                  <tr key={obj.object_id}>
                    <td>
                      {obj.image_url ? (
                        <img
                          src={obj.image_url}
                          alt={obj.name}
                          className="thumbnail-image"
                          title={obj.name}
                        />
                      ) : (
                        <div className="no-thumbnail">No Image</div>
                      )}
                    </td>
                    <td>{obj.name}</td>
                    <td>{obj.type}</td>
                    <td>{obj.category}</td>
                    <td>
                      {obj.tags && obj.tags.length > 0 ? (
                        <div className="tag-badges-container">
                          {obj.tags.map((tag, index) => (
                            <span key={index} className="badge bg-secondary">
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">
                          {t("admin.searchFieldOptions.noTags")}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => editItem("object", obj.object_id)}
                      >
                        {t("admin.edit")}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => confirmDelete("object", obj.object_id)}
                      >
                        {t("admin.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app">
      <Header />
      <div className="container admin-container mt-4">
        {/* Error and Success Display */}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            <strong>{t("common.error")}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {successMessage && (
          <div
            className="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            <strong>{t("common.success")}:</strong> {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        <header className="pb-3 mb-4 border-bottom">
          <div className="d-flex align-items-center text-dark text-decoration-none">
            <span className="fs-4">{t("admin.title")}</span>
            <div className="ms-auto d-flex align-items-center gap-2">
              <span className="badge bg-success">{process.env.NODE_ENV}</span>
              <span
                className={`badge ${
                  isAuthenticated ? "bg-success" : "bg-danger"
                }`}
              >
                {isAuthenticated
                  ? t("admin.authenticated")
                  : t("admin.notAuthenticated")}
              </span>
            </div>
          </div>
        </header>

        {/* Authentication Required Message */}
        {!isAuthenticated && (
          <div className="alert alert-warning" role="alert">
            <strong>{t("admin.authenticationRequired")}:</strong>{" "}
            {t("admin.authenticationRequiredMessage")}
          </div>
        )}

        <div className="row">
          <div className="col-md-3">
            <div className="card">
              <div className="card-header">{t("admin.navigation")}</div>
              <div className="card-body p-0">
                <div className="nav flex-column nav-pills">
                  <button
                    className={`nav-link ${
                      activeTab === "plants" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("plants")}
                  >
                    {t("admin.plants")}
                  </button>
                  <button
                    className={`nav-link ${
                      activeTab === "objects" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("objects")}
                  >
                    {t("admin.objects")}
                  </button>
                  <button
                    className={`nav-link ${
                      activeTab === "companies" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("companies")}
                  >
                    {t("admin.companies")}
                  </button>
                  <button
                    className={`nav-link ${
                      activeTab === "inventory" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("inventory")}
                  >
                    {t("admin.inventory")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-9">
            {/* Plants Tab */}
            {activeTab === "plants" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{t("admin.plantsManagement")}</span>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => createNewItem("plant")}
                  >
                    {t("admin.newPlant")}
                  </button>
                </div>
                <div className="card-body">
                  {getCurrentLoading() ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-success"
                        role="status"
                      >
                        <span className="visually-hidden">
                          {t("admin.loading")}
                        </span>
                      </div>
                      <p className="mt-2 text-muted">Loading plants...</p>
                    </div>
                  ) : (
                    renderPlantTable()
                  )}
                </div>
              </div>
            )}

            {/* Objects Tab */}
            {activeTab === "objects" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{t("admin.objectsManagement")}</span>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => createNewItem("object")}
                  >
                    {t("admin.newObject")}
                  </button>
                </div>
                <div className="card-body">
                  {getCurrentLoading() ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-success"
                        role="status"
                      >
                        <span className="visually-hidden">
                          {t("admin.loading")}
                        </span>
                      </div>
                      <p className="mt-2 text-muted">
                        Loading garden objects...
                      </p>
                    </div>
                  ) : (
                    renderObjectTable()
                  )}
                </div>
              </div>
            )}

            {/* Companies Tab */}
            {activeTab === "companies" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{t("admin.companiesManagement")}</span>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => createNewItem("company")}
                  >
                    {t("admin.newCompany")}
                  </button>
                </div>
                <div className="card-body">
                  {getCurrentLoading() ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-success"
                        role="status"
                      >
                        <span className="visually-hidden">
                          {t("admin.loading")}
                        </span>
                      </div>
                      <p className="mt-2 text-muted">Loading companies...</p>
                    </div>
                  ) : companies.length === 0 ? (
                    <div className="text-center py-5">
                      <p>{t("admin.noCompanies")}</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>{t("admin.name")}</th>
                            <th>{t("admin.contact")}</th>
                            <th>{t("admin.email")}</th>
                            <th>{t("admin.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companies.map((company) => (
                            <tr key={company.company_id}>
                              <td>{company.name}</td>
                              <td>{company.contact_name}</td>
                              <td>{company.contact_email}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() =>
                                    editItem("company", company.company_id)
                                  }
                                >
                                  {t("admin.edit")}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() =>
                                    confirmDelete("company", company.company_id)
                                  }
                                >
                                  {t("admin.delete")}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inventory Tab */}
            {activeTab === "inventory" && (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>{t("admin.inventoryManagement")}</span>
                  <div>
                    <select
                      className="form-select form-select-sm d-inline-block me-2"
                      style={{ width: "auto" }}
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                    >
                      {selectedCompany === "" ? (
                        <option value="">{t("admin.selectCompany")}</option>
                      ) : (
                        <></>
                      )}
                      {companies.map((company) => (
                        <option
                          key={company.company_id}
                          value={company.company_id}
                        >
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn btn-sm btn-success"
                      disabled={!selectedCompany}
                      onClick={() => createNewItem("inventory")}
                    >
                      {t("admin.addItem")}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {!selectedCompany ? (
                    <div className="text-center py-5">
                      <p>{t("admin.pleaseSelectCompany")}</p>
                    </div>
                  ) : getCurrentLoading() ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-success"
                        role="status"
                      >
                        <span className="visually-hidden">
                          {t("admin.loading")}
                        </span>
                      </div>
                      <p className="mt-2 text-muted">
                        Loading inventory items...
                      </p>
                    </div>
                  ) : inventory.length === 0 ? (
                    <div className="text-center py-5">
                      <p>{t("admin.noInventory")}</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>{t("admin.itemName")}</th>
                            <th>{t("admin.type")}</th>
                            <th>{t("admin.quantity")}</th>
                            <th>{t("admin.price")}</th>
                            <th>Status</th>
                            <th>{t("admin.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventory.map((item) => (
                            <tr key={item.inventory_id}>
                              <td>
                                {item.type === "plant"
                                  ? item?.plant_data?.common_name
                                  : item?.object_data?.name}
                              </td>
                              <td>{item.type}</td>
                              <td>{item.quantity}</td>
                              <td>
                                $
                                {typeof item.selling_price === "number"
                                  ? item.selling_price.toFixed(2)
                                  : parseFloat(
                                      item.selling_price || "0"
                                    ).toFixed(2)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    item.status === "in_stock"
                                      ? "bg-success"
                                      : item.status === "low_stock"
                                      ? "bg-warning"
                                      : item.status === "out_of_stock"
                                      ? "bg-danger"
                                      : item.status === "discontinued"
                                      ? "bg-secondary"
                                      : "bg-info"
                                  }`}
                                >
                                  {item.status?.replace(/_/g, " ") || "active"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() =>
                                    editItem(
                                      "inventory",
                                      item.inventory_id || ""
                                    )
                                  }
                                >
                                  {t("admin.edit")}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() =>
                                    confirmDelete(
                                      "inventory",
                                      item.inventory_id || ""
                                    )
                                  }
                                >
                                  {t("admin.delete")}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditModal
        isOpen={isEditModalOpen}
        onClose={closeModal}
        title={modalTitle}
        onSave={handleSave}
        isSaving={isSaving}
        error={saveError}
        saveButtonLabel={saveButtonLabel}
      >
        {editType === "plant" && (
          <PlantEditForm plant={selectedPlant} _onSave={setSelectedPlant} />
        )}
        {editType === "object" && (
          <ObjectEditForm
            object={selectedObject}
            _onSave={setSelectedObject}
            objectTypes={objectTypes}
            categories={categories}
            setObjectTypes={setObjectTypes}
            setCategories={setCategories}
          />
        )}
        {editType === "company" && (
          <CompanyEditForm
            ref={companyFormRef}
            company={selectedCompanyForEdit}
            _onSave={setSelectedCompanyForEdit}
          />
        )}
        {editType === "inventory" && (
          <InventoryEditForm
            ref={inventoryFormRef}
            item={selectedInventoryItem}
            _onSave={setSelectedInventoryItem}
            company_id={selectedCompany}
          />
        )}
      </EditModal>
    </div>
  );
};

export default Admin;
